// ============================================
// Cấu hình ngưỡng hạng (sheet 2 mục #45)
// ============================================
//
// Ngưỡng áp TOÀN CHUỖI theo từng tiền tệ — mỗi currency một form.
//
// Điểm dễ hiểu nhầm nhất của tính năng: lưu ngưỡng KHÔNG đổi hạng của khách
// hiện hữu. Vì thế màn hình nói ba lần, ở ba thời điểm khác nhau:
//   1. cảnh báo thường trực ở đầu khối,
//   2. thông báo ngay dưới form vừa lưu,
//   3. nút "Tính lại toàn bộ" đặt sẵn ở cả hai chỗ để không phải đi tìm.

import { useEffect, useState } from 'react';
import type { LoyaltyTierConfig } from '@conai/shared-types';
import { useI18n } from '../../../i18n';
import {
  useTierConfig,
  useUpdateTierConfig,
  useResetTierConfig,
  useRecalculateTiers,
  validateTierThresholds,
} from '../../../hooks';

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/** Nút "Tính lại toàn bộ" + bước xác nhận tại chỗ (thao tác chạy trên toàn bộ khách). */
function RecalculateButton({ compact }: { compact?: boolean }) {
  const { t } = useI18n();
  const [confirming, setConfirming] = useState(false);
  const { mutate: recalculate, isPending } = useRecalculateTiers();

  if (confirming) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-amber-900">{t('tierCfgRecalculateConfirm')}</span>
        <button
          type="button"
          disabled={isPending}
          onClick={() => recalculate(undefined, { onSettled: () => setConfirming(false) })}
          className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-60"
        >
          {isPending ? t('tierCfgRecalculateRunning') : t('tierCfgConfirmYes')}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
        >
          {t('tierCfgConfirmNo')}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      disabled={isPending}
      className={
        compact
          ? 'rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-60'
          : 'rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-60'
      }
    >
      {isPending ? t('tierCfgRecalculateRunning') : t('tierCfgRecalculate')}
    </button>
  );
}

function ThresholdInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <input
        type="number"
        min={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
      />
    </label>
  );
}

function TierConfigForm({ config }: { config: LoyaltyTierConfig }) {
  const { t } = useI18n();
  const { mutate: save, isPending: isSaving } = useUpdateTierConfig();
  const { mutate: reset, isPending: isResetting } = useResetTierConfig();

  const [silver, setSilver] = useState(String(config.silver_threshold));
  const [gold, setGold] = useState(String(config.gold_threshold));
  const [platinum, setPlatinum] = useState(String(config.platinum_threshold));
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  // Đồng bộ lại khi giá trị từ server đổi (sau khi lưu hoặc sau khi về mặc định).
  // Deps là ba con số nên lúc người dùng đang gõ mà query refetch ra đúng giá trị
  // cũ thì effect không chạy, không giật mất chữ đang nhập.
  useEffect(() => {
    setSilver(String(config.silver_threshold));
    setGold(String(config.gold_threshold));
    setPlatinum(String(config.platinum_threshold));
  }, [config.silver_threshold, config.gold_threshold, config.platinum_threshold]);

  const handleSave = () => {
    const payload = {
      silver_threshold: Number(silver),
      gold_threshold: Number(gold),
      platinum_threshold: Number(platinum),
    };
    const errorKey = validateTierThresholds(payload);
    if (errorKey) {
      setError(t(errorKey));
      setJustSaved(false);
      return;
    }
    setError(null);
    save(
      { currency: config.currency, data: payload },
      {
        onSuccess: () => setJustSaved(true),
        onError: (err: unknown) => {
          setJustSaved(false);
          setError(err instanceof Error ? err.message : t('tierCfgErrorGeneric'));
        },
      }
    );
  };

  const handleReset = () => {
    setError(null);
    reset(config.currency, {
      // Về mặc định cũng là ĐỔI NGƯỠNG, nên vẫn phải nhắc chuyện tính lại.
      onSuccess: () => setJustSaved(true),
      onError: (err: unknown) => {
        setJustSaved(false);
        setError(err instanceof Error ? err.message : t('tierCfgErrorGeneric'));
      },
    });
  };

  const isCustom = config.source === 'custom';

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-slate-900">{config.currency}</span>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
              isCustom ? 'bg-primary-50 text-primary-700' : 'bg-slate-100 text-slate-600'
            }`}
          >
            {isCustom ? t('tierCfgSourceCustom') : t('tierCfgSourceDefault')}
          </span>
        </div>
        <span className="text-xs text-slate-400">
          {t('tierCfgBronze')}: {config.bronze_threshold.toLocaleString()} — {t('tierCfgBronzeNote')}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ThresholdInput label={t('tierCfgSilver')} value={silver} onChange={setSilver} />
        <ThresholdInput label={t('tierCfgGold')} value={gold} onChange={setGold} />
        <ThresholdInput label={t('tierCfgPlatinum')} value={platinum} onChange={setPlatinum} />
      </div>

      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-60"
        >
          {isSaving ? t('tierCfgSaving') : t('tierCfgSave')}
        </button>
        {/* Chỉ dòng `custom` mới có gì để xoá — dòng mặc định thì nút này vô nghĩa. */}
        {isCustom && (
          <button
            type="button"
            onClick={handleReset}
            disabled={isResetting}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
          >
            {isResetting ? t('tierCfgResetting') : t('tierCfgReset')}
          </button>
        )}
      </div>

      {/* Nhắc ngay sau khi lưu: im lặng ở đây là admin tưởng đã xong việc. */}
      {justSaved && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
          <WarningIcon className="mt-0.5 shrink-0 text-amber-700" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-amber-900">{t('tierCfgSavedTitle')}</p>
            <p className="mt-0.5 text-xs text-amber-800">{t('tierCfgSavedBody')}</p>
            <div className="mt-2">
              <RecalculateButton compact />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function TierConfigSection() {
  const { t } = useI18n();
  const { data, isLoading, error } = useTierConfig();

  const configs = data ?? [];

  return (
    <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">{t('tierCfgTitle')}</h3>
          <p className="mt-0.5 text-sm text-slate-500">{t('tierCfgSubtitle')}</p>
        </div>
        <RecalculateButton />
      </div>

      {/* Cảnh báo thường trực, hiện cả trước khi người dùng chạm vào form. */}
      <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <WarningIcon className="mt-0.5 shrink-0 text-amber-700" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">{t('tierCfgWarnTitle')}</p>
          <p className="mt-0.5 text-xs text-amber-800">{t('tierCfgWarnBody')}</p>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('tierCfgLoadFailed')}</p>
          <p className="mt-1 text-xs text-red-600">
            {error instanceof Error ? error.message : ''}
          </p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      ) : configs.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-10 text-center text-sm text-slate-400">
          {t('tierCfgEmpty')}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {configs.map((config) => (
            <TierConfigForm key={config.currency} config={config} />
          ))}
        </div>
      )}
    </div>
  );
}
